/**
 * Desktop side rail (D-086, FigCraft page 53): a slim gold-outlined bar, fixed left. The logo, the five tabs (+ Admin
 * for admins, with what needs a look, D-196), Help at the foot. You opens from your name in the header across the top, as on the phone; the
 * membership pill and balances live in that header too. Width is `--rail-w` (tokens.css).
 */
import type { ReactElement } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./Icon";
import { BrandMark } from "./Header";
import { formatNumber } from "../lib/format";
import { useSession } from "../lib/session";
import type { TabKey } from "./TabBar";
import { railSection } from "../lib/railSection";
import { useShopNew } from "../lib/shopNew";

export const RAIL_WIDTH = 104;
export function useRailWidth(): number { return RAIL_WIDTH; }

// Which item lights up comes from one mapping (lib/railSection.ts, D-118), so sub-pages light their parent.
const ITEMS: { key: TabKey | "admin"; icon: string; label: string; to: string; tone: string }[] = [
  { key: "home", icon: "house", label: "Home", to: "/", tone: "ochre" },
  { key: "tournaments", icon: "trophy", label: "Tournaments", to: "/tournaments", tone: "ochre" },
  { key: "clubs", icon: "crest", label: "Clubs", to: "/clubs", tone: "lagoon" },
  { key: "friends", icon: "users", label: "Friends", to: "/friends", tone: "leaf" },
  { key: "shop", icon: "gift", label: "Shop", to: "/shop", tone: "coral" },
  { key: "admin", icon: "shield", label: "Admin", to: "/admin", tone: "ochre" },
];

export function Rail({ className = "" }: { className?: string }): ReactElement {
  const { admin, adminNeeds, unread } = useSession();
  const shopNew = useShopNew();
  const { pathname } = useLocation();
  const section = railSection(pathname);
  // Request counts on the item that holds them (D-064), as on the phone tab bar.
  const counts: Partial<Record<string, number>> = { friends: unread.friends, clubs: unread.clubs, admin: adminNeeds };
  return (
    <nav className={`siderail ${className}`} aria-label="Main" data-testid="siderail">
      <Link to="/" className="siderail__lockup" aria-label="DIGU Classic, Home"><BrandMark size={52} /></Link>
      <ul className="siderail__items">
        {ITEMS.filter((i) => i.key !== "admin" || admin).map((i) => {
          const on = section === i.key;
          const n = counts[i.key] ?? 0;
          return (
            <li key={i.key}>
              <Link to={i.to} className={`siderail__item ${on ? "siderail__item--active" : ""}`} aria-current={on ? "page" : undefined} aria-label={n > 0 ? `${i.label}, ${formatNumber(n)} new` : i.label}>
                <span className="siderail__icon" aria-hidden="true"><Icon name={i.icon} size={22} /></span>
                <span className="siderail__label" aria-hidden="true">{i.label}</span>
                {n > 0 ? <span className="siderail__count" aria-hidden="true" data-testid={`rail-count-${i.key}`}>{n > 99 ? "99+" : formatNumber(n)}</span>
                  : i.key === "shop" && shopNew ? <span className="siderail__dot" aria-hidden="true" data-testid="rail-dot-shop" /> : null}
              </Link>
            </li>
          );
        })}
      </ul>
      <Link to="/you/settings/help" className={`siderail__help ${section === "help" ? "siderail__help--active" : ""}`} aria-label="Help" aria-current={section === "help" ? "page" : undefined}><Icon name="circle-help" size={24} /></Link>
    </nav>
  );
}
